import { HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, from, map, Observable, of } from 'rxjs';

import { suppressGlobalErrorNavigation } from '../../core/interceptors/error/suppress-global-error';
import { DataResponse } from '../../core/models/data-response';
import { ConfigService } from '../../core/services/config/config.service';
import { UtilityService } from '../../core/services/utility/utility.service';
import {
  fileNameFromContentDisposition,
  readBlobAsText,
} from '../../shared/utils/file-download/file-download.utils';
import { PatientPortalAccessLogDetail } from '../models/access-log-detail';
import { AuditEventSearchRequest, AuditEventSummary } from '../models/activity-log';
import { AdminActionLogDetail } from '../models/admin-action-log-detail';

// Re-exported because every caller that names the request type also injects this service, so the
// two travel together. The other model types are imported from `../models/*` at their use sites.
export type { AuditEventSearchRequest };

/** A page of audit-event entries plus the server's total count (drives pagination). */
export interface AuditEventSearchResult {
  entries: AuditEventSummary[];
  totalRecordCount: number;
}

/** A downloaded export file: the CSV bytes plus the name to save it under. */
export interface AuditEventExportFile {
  blob: Blob;
  fileName: string;
}

const CSV_CONTENT_TYPE = 'text/csv';

/**
 * The export is the one endpoint that answers with either a file or a JSON envelope, so it asks for
 * both explicitly rather than relying on the server's default.
 */
const EXPORT_ACCEPT_HEADER = new HttpHeaders({ Accept: 'text/csv, application/json' });

/** Shown when a failed export carries no readable `ResponsePackage` message. */
export const EXPORT_FALLBACK_ERROR_MESSAGE = 'The export could not be completed. Please try again.';

/**
 * True when a 2xx export response is actually the CSV it is supposed to be.
 *
 * The documented contract is `text/csv` on 200 and a JSON envelope on 400, so status alone would
 * usually be enough — but "usually" is the wrong bar when the failure mode is handing someone an
 * error page named `audit-log-export-….csv` and calling it an audit report. A gateway interstitial
 * or a future server change that answers 200 with JSON is caught here instead.
 */
function isCsvResponse(response: HttpResponse<Blob>): boolean {
  const contentType = response.headers.get('Content-Type') ?? response.body?.type ?? '';
  return contentType.toLowerCase().includes(CSV_CONTENT_TYPE);
}

/**
 * Client-side fallback for the download's file name, used only when `Content-Disposition` is
 * unreadable. Matches the server's `audit-log-export-{yyyyMMddHHmmss}.csv` shape and, like the
 * server, stamps it in **UTC** so the name agrees with the file's own "Generated (UTC)" preamble.
 */
function composeExportFileName(): string {
  const stamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
  return `audit-log-export-${stamp}.csv`;
}

/**
 * Pull the first message out of a serialized `ResponsePackage` body, preferring `messages` (where
 * the API puts the text meant for the user, e.g. the over-ceiling "narrow the filters" advice) and
 * falling back to `errors`. Returns `null` for anything unparseable.
 */
function parseResponsePackageMessage(body: string): string | null {
  try {
    const parsed = JSON.parse(body) as Partial<DataResponse<unknown>>;
    return parsed.messages?.find(Boolean) ?? parsed.errors?.find(Boolean) ?? null;
  } catch {
    return null;
  }
}

@Injectable({
  providedIn: 'root',
})
export class ActivityLogsService {
  private utilityService = inject(UtilityService);
  private configService = inject(ConfigService);
  private config = this.configService.config;
  private readonly auditEventsEndpoint = `${this.config.adminDashboardApiUrl}/AuditEvents`;
  private readonly accessLogsEndpoint = `${this.config.adminDashboardApiUrl}/PatientPortalAccessLogs`;
  private readonly adminActionLogsEndpoint = `${this.config.adminDashboardApiUrl}/PatientPortalAdminActionLogs`;

  /**
   * Searches the system-wide audit-event feed (patient-data disclosures + admin actions),
   * returning the matching page along with the total record count for pagination.
   */
  searchAuditEvents(request: AuditEventSearchRequest): Observable<AuditEventSearchResult> {
    // Request the raw envelope so we can read `totalRecordCount` for pagination
    // (the same pattern the linking-requests search uses).
    const url = `${this.auditEventsEndpoint}/Search`;

    return this.utilityService
      .postRequest<
        DataResponse<AuditEventSummary[]>
      >(url, request, false, false, true, suppressGlobalErrorNavigation())
      .pipe(
        map((response) => ({
          // Defaulted rather than trusted: `result` is *typed* as an array, but that is a claim
          // about the payload, not a guarantee. A 200 carrying `"result": null` would otherwise
          // reach the table and throw on `.length`.
          entries: response.result ?? [],
          totalRecordCount: response.totalRecordCount,
        })),
      );
  }

  /**
   * Exports the audit-event feed as a CSV file, for the **same request the on-screen search was run
   * with**. The export honours that request's filters and sort but ignores its paging: it returns
   * every matching row across both categories, never just the current page.
   *
   * Unlike every other call here, success is a raw `text/csv` body rather than a `DataResponse<T>`
   * envelope, and a validation failure or an over-ceiling match is a 400 JSON `ResponsePackage`.
   * Callers must treat a failure as a message to surface, not a file to save — see
   * {@link readExportErrorMessage}.
   *
   * A 2xx that isn't `text/csv` is routed to the error channel rather than saved (see
   * {@link isCsvResponse}), so nothing reaches the caller unless it really is a file.
   *
   * The global error navigation is suppressed because a failed export is an actionable message on
   * the audit-log screen ("narrow the filters"), not a dead end — redirecting to `/error` would
   * strand the user away from the very filters they need to change. A 401 still redirects.
   */
  exportAuditEvents(request: AuditEventSearchRequest): Observable<AuditEventExportFile> {
    return this.utilityService
      .postFileRequest(
        `${this.auditEventsEndpoint}/Search/Export`,
        request,
        EXPORT_ACCEPT_HEADER,
        suppressGlobalErrorNavigation(),
      )
      .pipe(
        map((response) => {
          // Throwing from `map` routes to the error channel exactly as `throwError` would.
          if (!isCsvResponse(response)) {
            throw new Error('The export response was not a CSV file.');
          }

          return {
            // The server owns the file's bytes (UTF-8 BOM + CRLF, so Excel opens it cleanly); the
            // blob is passed through untouched rather than decoded and re-encoded, which is where a
            // BOM gets lost. An empty match is a valid preamble-and-header-only file, not an error.
            blob: response.body ?? new Blob([], { type: CSV_CONTENT_TYPE }),
            fileName:
              fileNameFromContentDisposition(response.headers.get('Content-Disposition')) ??
              composeExportFileName(),
          };
        }),
      );
  }

  /**
   * Resolve the user-facing message from a failed {@link exportAuditEvents} call.
   *
   * The export is requested as a blob, so its 400 `ResponsePackage` arrives as a `Blob` rather than
   * parsed JSON — it has to be read back to text and parsed by hand. Always emits a message and
   * never errors, so a malformed or unexpected body degrades to the generic text instead of leaving
   * the user with nothing.
   */
  readExportErrorMessage(error: unknown): Observable<string> {
    const body = error instanceof HttpErrorResponse ? error.error : null;
    if (!(body instanceof Blob)) {
      return of(EXPORT_FALLBACK_ERROR_MESSAGE);
    }

    return from(readBlobAsText(body)).pipe(
      map((text) => parseResponsePackageMessage(text) ?? EXPORT_FALLBACK_ERROR_MESSAGE),
      catchError(() => of(EXPORT_FALLBACK_ERROR_MESSAGE)),
    );
  }

  /**
   * Loads a single access-log (disclosure) event with its full set of disclosed records.
   * Reached from a disclosure row via its `sourceId` (which is the underlying access-log id).
   *
   * Emits `null` for an id the ledger doesn't have: the API answers a missing record with a
   * `RecordNotFound` envelope as well as with a 404, and the envelope's unwrapped payload is null.
   * Declared here so callers are made to handle it — a non-nullable signature understated the real
   * contract and let a consumer spin forever on the envelope form.
   */
  getAccessLogById(id: number): Observable<PatientPortalAccessLogDetail | null> {
    return this.utilityService.getRequest<PatientPortalAccessLogDetail>(
      `${this.accessLogsEndpoint}/${id}`,
      false,
      suppressGlobalErrorNavigation(),
    );
  }

  /**
   * Loads a single admin action with its note or decision context. Reached from an admin-action
   * row via its `sourceId` (which is the underlying admin-action-log id) — never from a
   * disclosure row, whose `sourceId` belongs to a different ledger.
   *
   * Emits `null` when the record doesn't exist: the API answers a missing id with a
   * `RecordNotFound` envelope carrying a null `result` as well as with a 404, so callers must
   * treat a null emission and a 404 error the same way.
   */
  getAdminActionLogById(id: number): Observable<AdminActionLogDetail | null> {
    return this.utilityService.getRequest<AdminActionLogDetail | null>(
      `${this.adminActionLogsEndpoint}/${id}`,
      false,
      suppressGlobalErrorNavigation(),
    );
  }
}
